import { ProjectKind } from 'model/ProjectKind';
import { SimpleModel } from 'model/SimpleModel';
import { Dayjs } from 'dayjs';
import { LangCodeEnum } from 'shared/enums/LangCodeEnum';

import { Category } from './Category';
import { Company } from './Company';
import { Distribution } from './Distribution';
import { Page } from './Page';
import { ProjectLayout } from './ProjectLayout';
import { ProjectSortBy } from './ProjectSortBy';
import { ProjectStatus } from './ProjectStatus';

export namespace Project {
  export interface Shape {
    id?: number;
    name?: string;
    categories?: Category[];
    status?: ProjectStatus;
    kind?: ProjectKind;
    company?: Company;
    liveStart?: Dayjs;
    liveStop?: Dayjs;
    pages?: Page[];
    layout?: ProjectLayout;
    distributions?: Distribution[];
    preferredLanguage?: LangCodeEnum | null;
  }
}

export class Project extends SimpleModel<Project.Shape> {
  static readonly STATUSES: ProjectStatus[] = [
    ProjectStatus.InDevelopment,
    ProjectStatus.Live,
    ProjectStatus.Closed,
    ProjectStatus.Deleted,
  ];

  static readonly SORT_BYS: ProjectSortBy[] = [ProjectSortBy.LastModified, ProjectSortBy.LiveDates];

  getId(): number {
    return this.get('id');
  }

  getName(): string {
    return this.get('name');
  }

  getCategories(): Category[] {
    return this.get('categories', []);
  }

  getStatus(): ProjectStatus {
    return this.get('status', ProjectStatus.InDevelopment);
  }

  getCompany(): Company {
    return this.get('company');
  }

  hasCompany(): boolean {
    return this.has('company');
  }

  setStatus(status: ProjectStatus): this {
    return this.set('status', status);
  }

  getKind(): ProjectKind {
    return this.get('kind');
  }

  isInDevelopment(): boolean {
    return this.getStatus() === ProjectStatus.InDevelopment;
  }

  isLive(): boolean {
    return this.getStatus() === ProjectStatus.Live;
  }

  isClosed(): boolean {
    return this.getStatus() === ProjectStatus.Closed;
  }

  isDeleted(): boolean {
    return this.getStatus() === ProjectStatus.Deleted;
  }

  isEditable(): boolean {
    return this.isInDevelopment();
  }

  /**
   * We can only fix some properties, not fully edit.
   * @returns {boolean}
   */
  isFixableOnly(): boolean {
    return this.isLive() || this.isClosed();
  }

  isCloseable(): boolean {
    return this.isInDevelopment() || this.isLive();
  }

  isDeletable(): boolean {
    return this.isInDevelopment() || this.isClosed();
  }

  getLiveStart(): Dayjs {
    return this.get('liveStart');
  }

  getLiveStop(): Dayjs {
    return this.get('liveStop');
  }

  getPages(): Page[] {
    return this.get('pages', []);
  }

  getLayout(): ProjectLayout {
    return this.get('layout');
  }

  getDistributions(): Distribution[] {
    return this.get('distributions', []);
  }

  getPreferredLanguage() {
    return this.get('preferredLanguage');
  }
}
